/**
 * Mar 3, 2026 · Purchase Order Status Report.
 *
 * Mirrors the Sales Order Status Report (SalesOrders.jsx · SoStatusReport):
 *   • Slim single-line KPI strip with qty + ₹ value roll-ups (Total /
 *     Received / Pending).
 *   • Filter chips (All / Open / Partial / Received / Cancelled) + date
 *     range (Today / Week / Month / FY) + free-text search.
 *   • One <tr> per PO line, first-line-of-group carries PO No / Date /
 *     Supplier; PO No renders through TwoLineDocNo (right-aligned).
 *   • Print / PDF + Export CSV using the shared helpers.
 *
 * Data source: `/api/purchase-orders/` — each PO row already carries
 * `.lines[]` with `qty_ordered / qty_received / qty_pending / unit_cost`.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { exportToCSV } from "@/lib/csv";
import { printDocument } from "@/lib/print";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import {
  MagnifyingGlassIcon, PrinterIcon, DownloadSimpleIcon,
} from "@phosphor-icons/react";

const fmtQty = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const fmtInr = (v, digits = 2) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const fmtInr0 = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_TONE = {
  "Received":            "bg-emerald-500/10 text-emerald-300",
  "Partially Received":  "bg-amber-500/10 text-amber-300",
  "Open":                "bg-sky-500/10 text-sky-300",
  "Approved":            "bg-emerald-500/10 text-emerald-300",
  "Draft":               "bg-zinc-500/10 text-zinc-300",
  "Cancelled":           "bg-zinc-500/10 text-zinc-400",
};

const ALL_COLS = [
  { k: "po_date",   label: "PO Date" },
  { k: "supplier",  label: "Supplier" },
  { k: "source",    label: "Source SO / WO" },
  { k: "line",      label: "Line" },
  { k: "rm_code",   label: "RM Code" },
  { k: "rm_name",   label: "RM Name" },
  { k: "uom",       label: "UOM" },
  { k: "order_qty", label: "Order Qty" },
  { k: "received",  label: "Received" },
  { k: "pending",   label: "Pending" },
  { k: "rate",      label: "Rate" },
  { k: "value",     label: "Value" },
  { k: "received_v",label: "Received Value" },
  { k: "pending_v", label: "Pending Value" },
  { k: "expected",  label: "Expected" },
  { k: "status",    label: "Status" },
];

export default function PoStatusReport() {
  const [pos, setPos] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [presetKey, setPresetKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.po_report.cols");
      if (raw) return new Set(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
    return new Set(ALL_COLS.map(c => c.k));
  });
  const [showColsMenu, setShowColsMenu] = useState(false);
  const toggleCol = (k) => setVisibleCols(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.po_report.cols", JSON.stringify(Array.from(next))); } catch (_e) { /* ignore */ }
    return next;
  });
  const showAllCols = () => {
    const next = new Set(ALL_COLS.map(c => c.k));
    setVisibleCols(next);
    try { localStorage.setItem("mes.po_report.cols", JSON.stringify(Array.from(next))); } catch (_e) { /* ignore */ }
  };
  const col = (k) => visibleCols.has(k);
  const totalCols = 2 + Array.from(visibleCols).length; // PO No + Actions + toggleables

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-orders/");
      setPos(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load Purchase Orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyPreset = (key) => {
    const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    let from = "", to = "";
    if (key === "today") { const t = fmt(now); from = t; to = t; }
    else if (key === "week") {
      const dow = (now.getDay() + 6) % 7;
      from = fmt(new Date(y, m, d - dow));
      to = fmt(now);
    } else if (key === "month") {
      from = fmt(new Date(y, m, 1));
      to = fmt(now);
    } else if (key === "fy") {
      const fyStart = m >= 3 ? y : y - 1;
      from = fmt(new Date(fyStart, 3, 1));
      to = fmt(new Date(fyStart + 1, 2, 31));
    }
    setFromDate(from); setToDate(to); setPresetKey(key);
  };

  const inRange = (d) => (!fromDate || (d || "") >= fromDate) && (!toDate || (d || "") <= toDate);

  const filtered = useMemo(() => pos.filter(p => {
    if (statusFilter !== "all" && (p.status || "").toLowerCase() !== statusFilter) return false;
    if (fromDate || toDate) {
      if (!inRange((p.po_date || "").slice(0, 10))) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const inRm = (p.lines || []).some(l => (l.rm_code || "").toLowerCase().includes(q) || (l.rm_name || "").toLowerCase().includes(q));
      const match = (p.po_no || "").toLowerCase().includes(q)
        || (p.supplier_name || "").toLowerCase().includes(q)
        || (p.source_work_order_no || "").toLowerCase().includes(q)
        || (p.source_sales_order_no || "").toLowerCase().includes(q)
        || inRm;
      if (!match) return false;
    }
    return true;
  }), [pos, statusFilter, fromDate, toDate, search]);

  const totals = useMemo(() => filtered.reduce((a, p) => {
    for (const l of (p.lines || [])) {
      const ord = Number(l.qty_ordered || l.qty_total || 0);
      const rcv = Number(l.qty_received || 0);
      const pen = Math.max(0, Number(l.qty_pending ?? (ord - rcv)) || 0);
      const rate = Number(l.unit_cost || 0);
      a.orders  += 0;
      a.totalQ  += ord;
      a.rcvQ    += rcv;
      a.pendQ   += pen;
      a.totalV  += ord * rate;
      a.rcvV    += rcv * rate;
      a.pendV   += pen * rate;
    }
    a.orders = filtered.length;
    return a;
  }, { orders: 0, totalQ: 0, rcvQ: 0, pendQ: 0, totalV: 0, rcvV: 0, pendV: 0 }),
    [filtered]);

  const dateRangeLabel = (fromDate || toDate) ? `${fromDate || "…"} → ${toDate || "…"}` : "All dates";

  const exportCsv = () => {
    const flat = [];
    filtered.forEach(p => {
      const lines = (p.lines || []);
      const iter = lines.length ? lines : [{}];
      iter.forEach(l => {
        const ord = Number(l.qty_ordered || l.qty_total || 0);
        const rcv = Number(l.qty_received || 0);
        const pen = Math.max(0, Number(l.qty_pending ?? (ord - rcv)) || 0);
        const rate = Number(l.unit_cost || 0);
        flat.push({
          po_no: p.po_no,
          po_date: (p.po_date || "").slice(0, 10),
          supplier_name: p.supplier_name || "",
          source_so: p.source_sales_order_no || "",
          source_wo: p.source_work_order_no || "",
          line_no: l.line_no || "",
          rm_code: l.rm_code || "",
          rm_name: l.rm_name || "",
          uom: l.uom || "",
          order_qty: ord,
          received: rcv,
          pending: pen,
          unit_cost: rate,
          line_value: ord * rate,
          received_value: rcv * rate,
          pending_value: pen * rate,
          expected_date: (p.expected_date || "").slice(0, 10),
          status: p.status || "",
        });
      });
    });
    exportToCSV("po-status-report.csv", flat, [
      { key: "po_no", label: "PO No" }, { key: "po_date", label: "PO Date" },
      { key: "supplier_name", label: "Supplier" },
      { key: "source_so", label: "Source SO" }, { key: "source_wo", label: "Source WO" },
      { key: "line_no", label: "Line" }, { key: "rm_code", label: "RM Code" },
      { key: "rm_name", label: "RM Name" }, { key: "uom", label: "UOM" },
      { key: "order_qty", label: "Order Qty" }, { key: "received", label: "Received" },
      { key: "pending", label: "Pending" }, { key: "unit_cost", label: "Rate" },
      { key: "line_value", label: "Value" }, { key: "received_value", label: "Received Value" },
      { key: "pending_value", label: "Pending Value" },
      { key: "expected_date", label: "Expected" }, { key: "status", label: "Status" },
    ]);
  };

  const printReport = () => {
    printDocument({
      kind: "Purchase Order Status Report",
      no: new Date().toISOString().slice(0, 19).replace("T", " "),
      meta: [
        { label: "Date Range", value: dateRangeLabel },
        { label: "Filter", value: statusFilter.toUpperCase() },
        { label: "Total POs", value: filtered.length },
        { label: "Total Qty", value: fmtQty(totals.totalQ) },
        { label: "Received", value: fmtQty(totals.rcvQ) },
        { label: "Pending", value: fmtQty(totals.pendQ) },
        { label: "Total Value", value: fmtInr(totals.totalV) },
        { label: "Received Value", value: fmtInr(totals.rcvV) },
        { label: "Pending Value", value: fmtInr(totals.pendV) },
      ],
      sections: [{
        heading: `PO Summary · ${filtered.length}`,
        columns: [
          { key: "po_no", label: "PO No", mono: true },
          { key: "po_date", label: "PO Date", mono: true },
          { key: "supplier_name", label: "Supplier" },
          { key: "status", label: "Status" },
          { key: "total_qty", label: "Order Qty", align: "right", fmt: v => fmtQty(v) },
          { key: "rcv_qty", label: "Received", align: "right", fmt: v => fmtQty(v) },
          { key: "pend_qty", label: "Pending", align: "right", fmt: v => fmtQty(v) },
          { key: "total_value", label: "Value", align: "right", fmt: v => fmtInr(v) },
        ],
        rows: filtered.map(p => {
          const t = (p.lines || []).reduce((a, l) => {
            const ord = Number(l.qty_ordered || l.qty_total || 0);
            const rcv = Number(l.qty_received || 0);
            const pen = Math.max(0, Number(l.qty_pending ?? (ord - rcv)) || 0);
            const rate = Number(l.unit_cost || 0);
            a.q += ord; a.r += rcv; a.p += pen; a.v += ord * rate;
            return a;
          }, { q: 0, r: 0, p: 0, v: 0 });
          return { po_no: p.po_no, po_date: (p.po_date || "").slice(0, 10),
                   supplier_name: p.supplier_name, status: p.status,
                   total_qty: t.q, rcv_qty: t.r, pend_qty: t.p, total_value: p.total_value ?? t.v };
        }),
        footerRow: {
          po_no: `Total ${filtered.length}`,
          total_qty: fmtQty(totals.totalQ), rcv_qty: fmtQty(totals.rcvQ),
          pend_qty: fmtQty(totals.pendQ), total_value: fmtInr(totals.totalV),
        },
      }],
      signatures: ["Prepared By", "Reviewed By", "Approved"],
    });
  };

  const cellCls = "font-mono tabular-nums";

  return (
    <div className="space-y-4">
      {/* Slim single-line KPI strip — matches SO Status Report */}
      <div
        data-testid="po-report-totals-strip"
        className="flex flex-wrap items-center gap-x-5 gap-y-1 px-3 py-1.5 border border-[var(--border)] bg-[var(--bg-1)] rounded-sm text-xs"
      >
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Orders</span>
        <span className={cellCls + " text-white font-semibold"} data-testid="po-report-total-orders">{filtered.length}</span>

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Total Qty</span>
        <span className={cellCls + " text-[var(--accent)] font-semibold"} data-testid="po-report-total-qty">{fmtQty(totals.totalQ)}</span>

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Received</span>
        <span className={cellCls + " text-emerald-300 font-semibold"} data-testid="po-report-total-received">{fmtQty(totals.rcvQ)}</span>
        {totals.totalQ > 0 && <span className="text-[9px] font-mono text-zinc-500 tabular-nums">({Math.round((totals.rcvQ / totals.totalQ) * 100)}%)</span>}

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending</span>
        <span className={cellCls + ` font-semibold ${totals.pendQ > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="po-report-total-pending">{fmtQty(totals.pendQ)}</span>

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Total Value</span>
        <span className={cellCls + " text-white font-semibold"} data-testid="po-report-total-value">{fmtInr0(totals.totalV)}</span>

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Received Value</span>
        <span className={cellCls + " text-emerald-300 font-semibold"} data-testid="po-report-received-value">{fmtInr0(totals.rcvV)}</span>

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending Value</span>
        <span className={cellCls + ` font-semibold ${totals.pendV > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="po-report-pending-value">{fmtInr0(totals.pendV)}</span>

        <span className="h-3 w-px bg-[var(--border)]" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">{dateRangeLabel} · {statusFilter}</span>
      </div>

      {/* Filter row — mirrors SO Status Report */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 mr-auto flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Filter</div>
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
            {[
              { k: "all",                  label: "All",       count: pos.length },
              { k: "open",                 label: "Open",      count: pos.filter(p => (p.status || "").toLowerCase() === "open").length },
              { k: "partially received",   label: "Partial",   count: pos.filter(p => (p.status || "").toLowerCase() === "partially received").length },
              { k: "received",             label: "Received",  count: pos.filter(p => (p.status || "").toLowerCase() === "received").length },
              { k: "cancelled",            label: "Cancelled", count: pos.filter(p => (p.status || "").toLowerCase() === "cancelled").length },
            ].map(t => (
              <button key={t.k} data-testid={`po-report-filter-${t.k.replace(/\s+/g, "-")}`} onClick={() => setStatusFilter(t.k)} className={`px-3 h-8 text-xs font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${statusFilter === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}>
                {t.label}<span className={`ml-1.5 font-mono text-[10px] ${statusFilter === t.k ? "opacity-80" : "text-zinc-500"}`}>{t.count}</span>
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">Date</div>
          <input data-testid="po-report-from" type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPresetKey(""); }} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          <span className="text-zinc-500 text-xs">→</span>
          <input data-testid="po-report-to" type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPresetKey(""); }} className="h-8 bg-[var(--bg-0)] border border-[var(--border)] px-2 rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden ml-1">
            {[
              { k: "today",  label: "Today" },
              { k: "week",   label: "This Week" },
              { k: "month",  label: "This Month" },
              { k: "fy",     label: "This FY" },
            ].map(p => {
              const active = presetKey === p.k;
              return (
                <button key={p.k} data-testid={`po-report-preset-${p.k}`} onClick={() => applyPreset(p.k)}
                  title={p.k === "fy" ? "Indian Financial Year · Apr–Mar" : ""}
                  className={`px-2.5 h-8 text-[11px] font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${active ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}>
                  {p.label}
                </button>
              );
            })}
          </div>
          {(fromDate || toDate) && (
            <button data-testid="po-report-clear-date" onClick={() => { setFromDate(""); setToDate(""); setPresetKey(""); }} className="h-8 px-2 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
          )}
          <div className="relative ml-2 w-64">
            <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="po-report-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="PO / Supplier / RM code / SO / WO…" className="h-8 w-full pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
          </div>
        </div>
        <button onClick={printReport} data-testid="po-report-print-btn" className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold rounded-sm flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print / PDF</button>
        <button onClick={exportCsv} data-testid="po-report-csv-btn" className="h-9 px-4 border border-[var(--border)] text-sm font-semibold rounded-sm flex items-center gap-2"><DownloadSimpleIcon size={14} weight="bold" />Export CSV</button>
        <div className="relative">
          <button data-testid="po-report-cols-btn" onClick={() => setShowColsMenu(v => !v)}
            className="h-9 px-3 border border-[var(--border)] text-xs text-zinc-300 hover:bg-[var(--bg-2)] rounded-sm flex items-center gap-2"
            title="Show/hide columns"><span className="font-mono">{visibleCols.size}/{ALL_COLS.length}</span>Columns</button>
          {showColsMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowColsMenu(false)} />
              <div data-testid="po-report-cols-menu" className="absolute right-0 top-full mt-1 z-50 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl w-56 py-1">
                <div className="px-3 py-1.5 border-b border-[var(--border)] flex items-center justify-between">
                  <span className="overline">Toggle Columns</span>
                  <button onClick={showAllCols} data-testid="po-report-cols-all" className="text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline">Show All</button>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {ALL_COLS.map(c => (
                    <label key={c.k} data-testid={`po-report-col-toggle-${c.k}`} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[var(--bg-2)]">
                      <input type="checkbox" checked={visibleCols.has(c.k)} onChange={() => toggleCol(c.k)} className="w-3.5 h-3.5 accent-[var(--accent)]" />
                      <span className="text-zinc-300">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Report table */}
      <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)] sticky top-0 z-20 bg-[var(--bg-1)]">
              <th className="text-right px-4 py-3">PO No</th>
              {col("po_date")   && <th className="text-left px-4 py-3">PO Date</th>}
              {col("supplier")  && <th className="text-left px-4 py-3">Supplier</th>}
              {col("source")    && <th className="text-left px-4 py-3">Source SO / WO</th>}
              {col("line")      && <th className="text-right px-4 py-3">Line</th>}
              {col("rm_code")   && <th className="text-left px-4 py-3">RM Code</th>}
              {col("rm_name")   && <th className="text-left px-4 py-3">RM Name</th>}
              {col("uom")       && <th className="text-left px-4 py-3">UOM</th>}
              {col("order_qty")&& <th className="text-right px-4 py-3">Order Qty</th>}
              {col("received") && <th className="text-right px-4 py-3">Received</th>}
              {col("pending")  && <th className="text-right px-4 py-3">Pending</th>}
              {col("rate")     && <th className="text-right px-4 py-3">Rate</th>}
              {col("value")    && <th className="text-right px-4 py-3">Value</th>}
              {col("received_v") && <th className="text-right px-4 py-3">Received Value</th>}
              {col("pending_v")  && <th className="text-right px-4 py-3">Pending Value</th>}
              {col("expected") && <th className="text-left px-4 py-3">Expected</th>}
              {col("status")   && <th className="text-left px-4 py-3">Status</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={totalCols} className="text-center py-14 text-zinc-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={totalCols} className="text-center py-14 text-zinc-500">No purchase orders match this filter.</td></tr>}
            {!loading && filtered.flatMap(p => {
              const today = new Date().toISOString().slice(0, 10);
              const lines = (p.lines && p.lines.length) ? p.lines : [null];
              const isOverdue = p.expected_date && (p.expected_date || "").slice(0, 10) < today
                && !(["received", "cancelled"].includes((p.status || "").toLowerCase()));
              return lines.map((l, lineIdx) => {
                const isFirst = lineIdx === 0;
                const isLast = lineIdx === lines.length - 1;
                const ord = Number(l?.qty_ordered || l?.qty_total || 0);
                const rcv = Number(l?.qty_received || 0);
                const pen = Math.max(0, Number(l?.qty_pending ?? (ord - rcv)) || 0);
                const rate = Number(l?.unit_cost || 0);
                const value = ord * rate;
                const receivedValue = rcv * rate;
                const pendingValue = pen * rate;
                const src = [p.source_sales_order_no, p.source_work_order_no].filter(Boolean).join(" · ");
                return (
                  <tr key={`${p.id}-${lineIdx}`}
                    data-testid={`po-report-row-${p.po_no}-${lineIdx}`}
                    className={`${!isLast ? "border-b border-[var(--border)]/40" : "border-b border-[var(--border)]"} align-top ${isOverdue ? "bg-amber-500/[0.05] hover:bg-amber-500/[0.08]" : "hover:bg-[var(--bg-2)]/40"}`}
                  >
                    <td className="px-4 py-3 font-mono text-right">
                      {isFirst
                        ? <TwoLineDocNo value={p.po_no} testId={`po-report-no-${p.po_no}`} className="text-white" />
                        : <span className="text-zinc-600">↳</span>}
                    </td>
                    {col("po_date")   && <td className="px-4 py-3 font-mono text-zinc-400">{isFirst ? (p.po_date || "").slice(0, 10) : ""}</td>}
                    {col("supplier")  && <td className="px-4 py-3">{isFirst ? (p.supplier_name || "—") : ""}</td>}
                    {col("source")    && <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{isFirst ? (src || "—") : ""}</td>}
                    {col("line")      && <td className="px-4 py-3 text-right font-mono text-zinc-500 text-xs tabular-nums">{l ? (l.line_no || (lineIdx + 1)) : "—"}</td>}
                    {col("rm_code")   && <td className="px-4 py-3 font-mono text-[var(--accent)] text-xs whitespace-nowrap" title={l?.rm_code || ""}>{l?.rm_code || "—"}</td>}
                    {col("rm_name")   && <td className="px-4 py-3 text-zinc-300 text-xs">{l?.rm_name || "—"}</td>}
                    {col("uom")       && <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{l?.uom || "—"}</td>}
                    {col("order_qty")&& <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums">{l ? fmtQty(ord) : "—"}</td>}
                    {col("received") && <td className="px-4 py-3 text-right font-mono text-emerald-400 tabular-nums">{l ? fmtQty(rcv) : "0"}</td>}
                    {col("pending")  && <td className={`px-4 py-3 text-right font-mono tabular-nums ${l && pen > 0 ? "text-amber-400" : "text-zinc-500"}`}>{l ? fmtQty(pen) : "0"}</td>}
                    {col("rate")     && <td className="px-4 py-3 text-right font-mono text-zinc-300 tabular-nums">{l ? fmtInr(rate) : "—"}</td>}
                    {col("value")    && <td className="px-4 py-3 text-right font-mono tabular-nums">{l ? fmtInr(value) : "—"}</td>}
                    {col("received_v") && <td className="px-4 py-3 text-right font-mono text-emerald-300 tabular-nums">{l ? fmtInr(receivedValue) : "—"}</td>}
                    {col("pending_v")  && <td className={`px-4 py-3 text-right font-mono tabular-nums ${l && pendingValue > 0 ? "text-amber-300" : "text-zinc-500"}`}>{l ? fmtInr(pendingValue) : "—"}</td>}
                    {col("expected") && <td className="px-4 py-3 font-mono">{isFirst ? (
                      // Mar 3, 2026 · Line-level expected_date takes precedence
                      // when set (per SO/ESO parity). Header falls back.
                      ((l?.expected_date) || p.expected_date || "") ? (
                        <span className={`font-mono text-xs ${isOverdue ? "text-amber-400 font-semibold" : "text-zinc-400"}`}>{((l?.expected_date) || p.expected_date || "").slice(0, 10)}</span>
                      ) : <span className="text-zinc-500">—</span>
                    ) : (
                      l?.expected_date && l.expected_date !== p.expected_date
                        ? <span className="font-mono text-xs text-amber-400">{(l.expected_date || "").slice(0, 10)}</span>
                        : ""
                    )}</td>}
                    {col("status")   && <td className="px-4 py-3">{isFirst ? (
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${STATUS_TONE[p.status] || "bg-zinc-500/10 text-zinc-300"}`}>
                        {p.status || "—"}
                      </span>
                    ) : ""}</td>}
                  </tr>
                );
              });
            })}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot className="bg-[var(--bg-2)]">
              <tr className="font-semibold">
                <td colSpan={col("po_date") + col("supplier") + col("source") + col("line") + col("rm_code") + col("rm_name") + col("uom") + 1}
                    className="px-4 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Totals</td>
                {col("order_qty") && <td className="px-4 py-3 text-right font-mono font-semibold" data-testid="po-report-footer-order-qty">{fmtQty(totals.totalQ)}</td>}
                {col("received")  && <td className="px-4 py-3 text-right font-mono text-emerald-400">{fmtQty(totals.rcvQ)}</td>}
                {col("pending")   && <td className="px-4 py-3 text-right font-mono text-amber-400">{fmtQty(totals.pendQ)}</td>}
                {col("rate")      && <td></td>}
                {col("value")     && <td className="px-4 py-3 text-right font-mono">{fmtInr(totals.totalV)}</td>}
                {col("received_v")&& <td className="px-4 py-3 text-right font-mono text-emerald-300">{fmtInr(totals.rcvV)}</td>}
                {col("pending_v") && <td className="px-4 py-3 text-right font-mono text-amber-300">{fmtInr(totals.pendV)}</td>}
                {col("expected")  && <td></td>}
                {col("status")    && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
